import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Minimal add-to-cart for the cart drawer upsell (no product-form / fly animation).
 * Dispatches CartAddEvent so cart-items-component morphs the header section like PDP quick-add.
 * Optional data-img: sent as hidden line item property "_img" (custom cart thumbnail).
 */
class CartDrawerUpsellComponent extends HTMLElement {
  /** @type {AbortController | undefined} */
  #clickAbort;

  connectedCallback() {
    const button = this.querySelector('[data-cart-drawer-upsell-submit]');
    if (!(button instanceof HTMLButtonElement)) return;

    this.#clickAbort?.abort();
    this.#clickAbort = new AbortController();
    button.addEventListener('click', this.#onSubmit, { signal: this.#clickAbort.signal });
  }

  disconnectedCallback() {
    this.#clickAbort?.abort();
  }

  #onSubmit = async (/** @type {MouseEvent} */ event) => {
    event.preventDefault();

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-cart-drawer-upsell-submit]'));
    const errEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-upsell-error]'));
    if (!button || button.disabled) return;

    const variantId = this.dataset.variantId;
    const sectionId = this.dataset.sectionId;
    const quantity = Math.max(1, Number(this.dataset.quantity) || 1);
    const img = this.dataset.upsellImage || this.dataset.img;

    if (!variantId || !sectionId) return;

    errEl?.classList.add('hidden');
    if (errEl) errEl.textContent = '';

    button.disabled = true;

    try {
      const sectionIds = /** @type {string[]} */ ([]);
      document.querySelectorAll('cart-items-component').forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });

      /** @type {{ id: number, quantity: number, properties?: Record<string, string> }} */
      const line = { id: Number(variantId), quantity };
      if (img) line.properties = { _img: img };

      const payload = {
        items: [line],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      };

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const data = await response.json();

      if (data.status && data.message) {
        if (errEl) {
          errEl.textContent = data.message;
          errEl.classList.remove('hidden');
        }
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(data, this.id || 'cart-drawer-upsell', {
          source: 'cart-drawer-upsell',
          itemCount: data.item_count ?? quantity,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (e) {
      console.error(e);
      if (errEl) {
        const fallback = this.dataset.errorGeneric;
        errEl.textContent = fallback || 'Could not add to cart.';
        errEl.classList.remove('hidden');
      }
    } finally {
      button.disabled = false;
    }
  };
}

if (!customElements.get('cart-drawer-upsell-component')) {
  customElements.define('cart-drawer-upsell-component', CartDrawerUpsellComponent);
}
